import OpenInFullOutlinedIcon from '@mui/icons-material/OpenInFullOutlined';
import { TextButton, useModal } from '@shape-ds/components';
import { useTranslation } from 'react-i18next';
import './style.scss';
import { useEffect } from 'react';
import ButtonLink from 'components/ExternalLink/ExternalLink';
import { useTabs } from 'hooks/useTabs';
import { dayjs } from 'lib';
import { ChartContextProvider } from '@shape/lighthouse-components';
import FeaturesCharts from 'components/LineCharts/Features/FeaturesCharts';
import { EventItem } from 'queries/events/types';
import RiskScoreChart from 'components/LineCharts/Risk Score/RiskScoreChart';
import SensorsCharts from 'components/LineCharts/Sensors/SensorsCharts';
import { ExpandedChartsModal } from './components/ExpandedChartsModal/ExpandedChartsModal';
import { RadialScalingWrapper } from './components/RadialScalingWrapper';
import { PlotScalingWrapper } from './components/PlotScalingWrapper';
import { useExternalReference } from '../../hooks/useEventsData';
import { IS_DEMO } from 'config/constants';

interface ChartsAreaProps {
  assetId: string;
  tabs: string[];
  showExpandBtn?: boolean;
  selectedRow: EventItem;
  alarmsInfo: {
    alarms: {
      start: string;
      end: string;
    }[];
    alarmDateRange: {
      start: dayjs.Dayjs;
      end: dayjs.Dayjs;
    };
    closedAtAlarms: string[];
  };
  preSelectedTab?: string;
  showTitle?: boolean;
}

export const ChartsArea = ({
  assetId,
  tabs,
  showExpandBtn = true,
  selectedRow,
  alarmsInfo: { alarms, alarmDateRange, closedAtAlarms },
  preSelectedTab,
  showTitle = true,
}: ChartsAreaProps) => {
  const { t } = useTranslation();
  const { openModal } = useModal();
  const PIUrl = useExternalReference(selectedRow?.asset?.id, { name: 'Link to PI Vision' });
  const { activeTab, tabsComponent, setActiveTab } = useTabs({
    tabNames: tabs,
    getLabel: (key) => t(`event_page:charts_tabs.${key}`),
  });
  useEffect(() => {
    if (preSelectedTab) {
      setActiveTab(tabs.findIndex((tab) => tab === preSelectedTab));
    }
  }, [preSelectedTab]);

  const renderChart = () => {
    switch (activeTab) {
      case 'feature': {
        return (
          <ChartContextProvider
            alarmDateRange={alarmDateRange}
            defaultPeriod={'alarm'}
            chartId={`event-feature-${selectedRow.id}`}
            key="features"
          >
            <FeaturesCharts
              assetId={assetId}
              hasLegendCategoryFilter
              enableCustomRange
              alarmsInfo={{ alarmsData: alarms, closedAtAlarms }}
              modelName={selectedRow.model_name}
            />
          </ChartContextProvider>
        );
      }
      case 'sensor': {
        const defaultDemoPeriod = selectedRow.model_name?.includes('ShapeIntel')
          ? String(24 * 365)
          : '168';
        return (
          <ChartContextProvider
            alarmDateRange={alarmDateRange}
            defaultPeriod={IS_DEMO ? defaultDemoPeriod : 'alarm'}
            chartId={`event-sensors-${selectedRow.id}`}
            key="sensors"
          >
            <SensorsCharts
              assetId={assetId}
              eventId={selectedRow.id}
              tagsBy="event"
              showExcludeGap={false}
              alarmsInfo={{
                alarmsData: alarms,
                closedAtAlarms,
              }}
              enableCustomRange
              hasLegendCategoryFilter
            />
          </ChartContextProvider>
        );
      }
      case 'radar':
        if (!selectedRow.model_name) return null;
        return (
          <RadialScalingWrapper
            modelName={selectedRow.model_name}
            alarmDateRange={{
              end: selectedRow.alarm_datetime_end,
              start: selectedRow.alarm_datetime_start,
            }}
            showTitle={showTitle}
          />
        );
      case 'amoeba':
        if (!selectedRow.model_name) return null;
        return (
          <PlotScalingWrapper
            modelName={selectedRow.model_name}
            alarmDateRange={{
              end: dayjs(selectedRow.alarm_datetime_end),
              start: dayjs(selectedRow.alarm_datetime_start),
            }}
            showTitle={showTitle}
          />
        );
      case 'risk':
        return (
          <ChartContextProvider
            alarmDateRange={alarmDateRange}
            defaultPeriod={'alarm'}
            chartId={`event-risk-${selectedRow.model_name}`}
          >
            <RiskScoreChart
              modelName={selectedRow.model_name}
              alarmsInfo={{ alarmsData: alarms, closedAtAlarms }}
              hasAlarmPeriod
              showTitle={showTitle}
            />
          </ChartContextProvider>
        );
      default:
        return null;
    }
  };

  return (
    <div className="chart-area-wrapper">
      <div className="chart-tabs">
        {tabsComponent}
        <div className="links">
          {showExpandBtn && (
            <TextButton
              className="expand-link"
              startIcon={<OpenInFullOutlinedIcon />}
              onClick={() => {
                openModal(
                  <ExpandedChartsModal
                    assetId={assetId}
                    selectedRow={selectedRow}
                    charts={tabs}
                    activeTab={activeTab}
                  />,
                  {
                    width: '90%',
                  },
                );
              }}
            >
              {t('event_page:components.btns.Expand')}
            </TextButton>
          )}
          {PIUrl && <ButtonLink label={t('event_page:components.btns.PI')} url={PIUrl} />}
        </div>
      </div>
      {renderChart()}
    </div>
  );
};
